/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Boris Kudlač (kudlac@cadwork.cz)
 * @modified by Jaroslav Přibyl (pribyl@cadwork.cz)
 */

import WebGLRenderGraph, {ERenderFiler} from "App/render/webgl/render_graph";
import RenderNode from "App/render/webgl/nodes/render_node";
import RenderObject from "App/geometry/objects/object";
import ModelStatistics from "App/model_statistics";
import Pass from "App/render/webgl/passes/pass";
import {ERenderableFilters} from "App/render/webgl/render_state";
import Factory from "App/render/webgl/nodes/factory";
import RenderablesFactory from "App/render/webgl/renderables/factory";
import Renderable from "App/render/webgl/renderables/renderable";
import RenderOption from "App/render/webgl/renderables/render_option";
import PointRenderOption from "App/render/webgl/renderables/point_render_option";
import LineRenderOption from "App/render/webgl/renderables/line_render_option";
import {RenderOptionNodeTypeName} from "App/render/webgl/nodes/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class RenderOptionNode extends RenderNode {

    public static TypeName: string = RenderOptionNodeTypeName;
    private _renderables: Renderable[];

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode, aShape: RenderObject) {
        super(aSceneAPI, aRenderGraph, aParentNode, aShape);

        this._typeName = RenderOptionNode.TypeName;

        this._renderables = [];

        // based on appearance material type we create appropriate renderable
        if (this.appearance.renderOption) {
            let renderable = RenderablesFactory.create(RenderOption, aSceneAPI, aRenderGraph, this) as RenderOption;
            renderable.setProperties(this.appearance.renderOption);
            this._renderables.push(renderable);
        }

        if (this.appearance.pointProperties) {
            let renderable = RenderablesFactory.create(PointRenderOption, aSceneAPI, aRenderGraph, this) as PointRenderOption;
            renderable.setProperties(this.appearance.pointProperties);
            this._renderables.push(renderable);
        }

        if (this.appearance.lineProperties) {
            let renderable = RenderablesFactory.create(LineRenderOption, aSceneAPI, aRenderGraph, this) as LineRenderOption;
            renderable.setProperties(this.appearance.lineProperties);
            this._renderables.push(renderable);
        }

        this._initFilter();
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode, aShape: RenderObject) {
        return new RenderOptionNode(aSceneAPI, aRenderGraph, aParentNode, aShape);
    }

    public release() {
        super.release();

        for (let renderable of this._renderables) {
            renderable.release();
        }
        this._renderables = [];
    }

    public compareProperties(aShape: RenderObject): boolean {
        return aShape.appearance.renderOption === this.appearance.renderOption;
    }

    public countShapesAndUniqueTextures(aStatistics: ModelStatistics) {
        aStatistics.addRenderOptionNode();

        super.countShapesAndUniqueTextures(aStatistics);
    }

    public render(aRenderPass: Pass) {
        let state = aRenderPass.state;
        let filter = state.getRenderFilterValue(RenderOptionNode.TypeName);

        // solve forcing keep camera above all layers
        if (this._sceneAPI.renderSettingsModule.getForceKeepCameraAboveAllLayers()) {
            if (filter !== 0 && (filter & (this.renderFilter | ERenderFiler.keepCameraAbove)) !== filter) {
                return false;
            }
        }
        else {
            // compare the running options to decide what kind of render pass we have
            // and allow to render children of this node only in case the the running options of this node contains
            // the running options which has been set up in the renderPass
            if (filter !== 0 && (filter & this.renderFilter) !== filter) {
                return false;
            }
        }

        if (state.getRenderableFilterIsSet(ERenderableFilters.renderOptions)) {
            for (let renderable of this._renderables) {
                renderable.render(aRenderPass);
            }
        }

        super.render(aRenderPass);
    }

    private _initFilter() {
        this.renderFilter = 0;
        if (!this.appearance.renderOption) {
            return;
        }

        if (this.appearance.renderOption.keepCameraAbove) {
            this.renderFilter |= ERenderFiler.keepCameraAbove;
        }

        if (this.appearance.renderOption.pickable) {
            this.renderFilter |= ERenderFiler.pickable;
        }

        if (this.appearance.renderOption.alwaysOnTop) {
            this.renderFilter |= ERenderFiler.alwaysOnTop;
        }

        if (this.appearance.renderOption.isCap) {
            this.renderFilter |= ERenderFiler.isCap;
        }

        if (this.appearance.renderOption.isSide) {
            this.renderFilter |= ERenderFiler.isSide;
        }
    }
}

Factory.register(RenderOptionNode);
